import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { createGuardWithTeam } from '../../services/guardService';
import { parseExcelFile } from '../../services/excelService';
import { UploadStats, ProcessedGuard } from './types';

export const useUploadProcessor = (onSuccess: () => void) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showReport, setShowReport] = useState(false);
  const [stats, setStats] = useState<UploadStats>({
    total: 0,
    processed: 0,
    created: 0,
    updated: 0,
    warnings: 0,
    errors: 0,
    detailedResults: []
  });
  const { toast } = useToast();

  // Improved batch processing with optimized performance
  const processInBatches = async (jsonData: any[]) => {
    // Increase batch size for better performance
    const batchSize = 5;
    const total = jsonData.length;
    let completed = 0;
    let errors = 0;
    let warnings = 0;
    let updated = 0;
    let created = 0;
    const detailedResults: ProcessedGuard[] = [];
    
    // Update stats initially
    setStats({
      total,
      processed: 0,
      created: 0,
      updated: 0,
      warnings: 0,
      errors: 0,
      detailedResults: []
    });

    // Reduce pause time to speed up processing
    const pauseTime = total > 100 ? 12000 : 8000; // Reduced from 25s/15s to 12s/8s

    for (let i = 0; i < jsonData.length; i += batchSize) {
      const batch = jsonData.slice(i, i + batchSize);
      
      // Process batch in parallel for speed
      const batchPromises = batch.map(async (row) => {
        try {
          // Check for missing required fields
          if (!row['Όνομα'] || !row['Επώνυμο']) {
            errors++;
            detailedResults.push({
              status: 'error',
              name: `${row['Όνομα'] || ''} ${row['Επώνυμο'] || ''}`.trim() || 'Unknown',
              error: 'Missing required field: First Name (Όνομα) and Last Name (Επώνυμο) are required'
            });
            
            return { status: 'error', data: null };
          }
          
          // Process the guard
          const result = await createGuardWithTeam(row, row['Ομάδα']);
          
          // Check if the guard was updated or created new
          if (result.teamAssignmentWarning || result.id) {
            completed++;
            
            // If already existed and was updated
            if (row['ΑΦΜ'] && result.teamAssignmentWarning) {
              updated++;
              
              // Add to detailed results
              detailedResults.push({
                status: 'updated',
                name: `${row['Όνομα']} ${row['Επώνυμο']}`,
                afm: row['ΑΦΜ'],
                warning: result.teamAssignmentWarning
              });
              
              return { status: 'updated', data: result };
            } else {
              created++;
              
              // Add to detailed results
              detailedResults.push({
                status: 'created',
                name: `${row['Όνομα']} ${row['Επώνυμο']}`,
                afm: row['ΑΦΜ']
              });
              
              return { status: 'created', data: result };
            }
          }
          
          // Check for team assignment warnings
          if (result.teamAssignmentWarning) {
            warnings++;
            
            detailedResults.push({
              status: 'warning',
              name: `${row['Όνομα']} ${row['Επώνυμο']}`,
              afm: row['ΑΦΜ'],
              warning: result.teamAssignmentWarning
            });
            
            return { status: 'warning', data: result };
          }
          
          return { status: 'unknown', data: result };
        } catch (rowError: any) {
          const errorMessage = rowError.message || 'Unknown error';
          
          // Check if this is a team-related warning that we want to treat as a non-fatal error
          if (errorMessage.includes('team assignment failed') || 
              errorMessage.includes('Failed to find team')) {
            warnings++;
            
            // Add to detailed results
            detailedResults.push({
              status: 'warning',
              name: `${row['Όνομα']} ${row['Επώνυμο']}`,
              afm: row['ΑΦΜ'],
              warning: errorMessage
            });
            
            // Still count as completed since the guard was created
            completed++;
            created++;
            
            return { status: 'warning', data: null };
          } else {
            errors++;
            
            // Add to detailed results
            detailedResults.push({
              status: 'error',
              name: `${row['Όνομα']} ${row['Επώνυμο']}`,
              afm: row['ΑΦΜ'],
              error: errorMessage
            });
            
            return { status: 'error', data: null };
          }
        }
      });
      
      // Wait for all promises in the batch to resolve
      await Promise.all(batchPromises);
      
      // Update progress and stats after each batch
      setProgress(Math.round((i + batchSize) / total * 100));
      setStats(prev => ({ 
        ...prev, 
        processed: completed,
        created,
        updated,
        warnings,
        errors,
        detailedResults: [...detailedResults]
      }));

      // Add a shorter delay between batches for large uploads
      if (i + batchSize < jsonData.length) {
        // Progress update toast for large batches
        if (total > 30 && (i % 20 === 0 || i === batchSize)) {
          toast({
            title: "Processing Batch",
            description: `Processed ${i + batchSize}/${total} guards (${created} new, ${updated} updated, ${warnings} warnings, ${errors} errors)`,
            variant: "default",
          });
        }
        
        // Show pause message for large batches
        if (total > 10) {
          toast({
            title: "Processing Pause",
            description: `Pausing for ${pauseTime/1000} seconds to avoid rate limits (${i + batchSize}/${jsonData.length} guards processed)`,
            variant: "default",
          });
        }
        
        // Shorter pause between batches
        await new Promise(resolve => setTimeout(resolve, pauseTime));
      }
    }

    return { 
      completed, 
      errors, 
      warnings, 
      created, 
      updated, 
      total,
      detailedResults
    };
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setProgress(0);
      setShowReport(false);
      
      const jsonData = await parseExcelFile(file);
      
      // Enhanced warning for large uploads
      if (jsonData.length > 50) {
        toast({
          title: "Large Upload Detected",
          description: `You're uploading ${jsonData.length} guards. This will take some time and include pauses to respect rate limits. Please be patient.`,
          variant: "default",
        });
      }
      
      const { 
        completed, 
        errors, 
        warnings, 
        created, 
        updated, 
        total,
        detailedResults 
      } = await processInBatches(jsonData);

      let toastVariant: "default" | "destructive" = "default";
      let title = "Upload Successful";
      let description = `Successfully processed ${completed} out of ${total} guards (${created} new, ${updated} updated).`;
      
      if (errors > 0) {
        toastVariant = "destructive";
        title = "Upload Completed with Errors";
        description = `Successfully processed ${completed} out of ${total} guards (${created} new, ${updated} updated). Failed to process ${errors} guards.`;
      } else if (warnings > 0) {
        title = "Upload Completed with Warnings";
        description = `Successfully processed ${completed} out of ${total} guards (${created} new, ${updated} updated). ${warnings} guards had team assignment issues.`;
      }

      toast({
        title,
        description,
        variant: toastVariant
      });

      // Update the stats with the final detailed results
      setStats(prev => ({
        ...prev,
        processed: completed,
        created,
        updated,
        warnings,
        errors,
        detailedResults
      }));

      // Show the report
      setShowReport(true);

      if (completed > 0) {
        onSuccess();
      }

    } catch (error: any) {
      console.error('Error handling file upload:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to upload guards.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetStats = () => {
    setProgress(0);
    setShowReport(false);
    setStats({
      total: 0,
      processed: 0,
      created: 0,
      updated: 0,
      warnings: 0,
      errors: 0,
      detailedResults: []
    });
  };

  return {
    isUploading,
    progress,
    showReport,
    stats,
    handleFileUpload,
    setShowReport,
    resetStats
  };
};
